import ExcelJS from 'exceljs';

export async function exportLayeredExcelWithExcelJS(config) {
  const {
    title = "动态报表",
    firstHeader = [],
    firstData = [],
    secondHeader = [],
    secondData = [],
    fileName = "分层报表.xlsx",
    sheetName = "数据报表"
  } = config;

  // 创建 workbook
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  // 全局统一边框样式
  const THIN_BORDER = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' }
  };
  const EMPTY_ROW_BORDER = {
    top: { style: 'thin' },
    left: { style: 'none' },
    bottom: { style: 'thin' },
    right: { style: 'none' }
  };
  // 无边框样式（彻底清空）
  const NO_BORDER = {
    top: { style: 'none' },
    left: { style: 'none' },
    bottom: { style: 'none' },
    right: { style: 'none' }
  };

  // 计算实际需要的列数
  const firstHeaderTotalCols = firstHeader.reduce((sum, item) => sum + (item.span || 1), 0);
  const secondHeaderTotalCols = secondHeader.reduce((sum, item) => sum + (item.span || 1), 0);
  const totalCols = Math.max(firstHeaderTotalCols, secondHeaderTotalCols);

  // 标题行
  worksheet.mergeCells(1, 1, 1, totalCols);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.style = {
    font: { bold: true, size: 14 },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E2F3' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
    border: THIN_BORDER
  };

  // 一级表头
  let colIndex = 1;
  firstHeader.forEach((item, index) => {
    const colspan = item.span || 1;
    const isRemarkCol = item.name === '备注';
    const endCol = colIndex + colspan - 1;

    if (colspan > 1) {
      worksheet.mergeCells(2, colIndex, 2, endCol);
    }

    const cell = worksheet.getCell(2, colIndex);
    cell.value = item.name;
    cell.style = {
      font: { bold: true },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6E6FA' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: THIN_BORDER
    };

    // 批量设置表头合并列边框
    if (colspan > 1) {
      for (let i = colIndex; i <= endCol; i++) {
        const mergeCell = worksheet.getCell(2, i);
        mergeCell.style.border = THIN_BORDER;
        if (isRemarkCol && i === endCol) {
          mergeCell.style.border.right = { style: 'thin' };
        }
      }
    }

    colIndex += colspan;
  });

  // ========== 核心修复：彻底消除firstData多余下边框 ==========
  colIndex = 1;
  let firstDataLastCol = 0; // 记录firstData实际占用的最后一列
  firstHeader.forEach((headerItem, headerIndex) => {
    const colspan = headerItem.span || 1;
    const isRemarkCol = headerItem.name === '备注';
    const endCol = colIndex + colspan - 1;
    if (colIndex > totalCols) return;

    // 合并数据行单元格
    if (colspan > 1) {
      worksheet.mergeCells(3, colIndex, 3, endCol);
    }

    // 设置数据单元格内容和样式
    const cell = worksheet.getCell(3, colIndex);
    cell.value = firstData[headerIndex] || '';
    cell.style = {
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: THIN_BORDER
    };

    // 批量设置合并列空单元格边框（仅实际内容范围）
    if (colspan > 1) {
      for (let i = colIndex + 1; i <= endCol; i++) {
        const emptyCell = worksheet.getCell(3, i);
        emptyCell.style.border = THIN_BORDER;
        if (isRemarkCol && i === endCol) {
          emptyCell.style.border.right = { style: 'thin' };
        }
      }
    }

    firstDataLastCol = endCol; // 更新实际最后一列
    colIndex += colspan;
  });

  // 关键修复1：清空超出列的所有边框（包括下边框）
  if (firstDataLastCol < totalCols) {
    for (let i = firstDataLastCol + 1; i <= totalCols; i++) {
      const cell = worksheet.getCell(3, i);
      cell.style.border = NO_BORDER; // 彻底清空所有边框
      cell.value = '';
      // 额外重置单元格的行/列边框继承
      cell.style = { ...cell.style, border: NO_BORDER };
    }
  }

  // 关键修复2：重置firstData行（第3行）的下边框范围
  const firstDataRow = worksheet.getRow(3);
  // 先清空整行的默认边框样式
  firstDataRow.style = { border: NO_BORDER };
  // 仅给实际内容列重新设置下边框
  for (let i = 1; i <= firstDataLastCol; i++) {
    const cell = worksheet.getCell(3, i);
    // 保留原有样式，仅确保下边框存在（覆盖行级重置）
    cell.style.border = {
      ...cell.style.border,
      bottom: { style: 'thin' }
    };
  }

  // 空行（第4行）- 避免和firstData行下边框冲突
  for (let i = 1; i <= totalCols; i++) {
    const cell = worksheet.getCell(4, i);
    cell.style.border = EMPTY_ROW_BORDER;
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    // 空行上边框和firstData行下边框重合，强制设为thin避免双层
    cell.style.border.top = { style: 'none' };
  }

  // 二级表头（第5行）
  colIndex = 1;
  secondHeader.forEach(item => {
    const colspan = item.span || 1;
    const endCol = colIndex + colspan - 1;
    if (colspan > 1) {
      worksheet.mergeCells(5, colIndex, 5, endCol);
    }

    const cell = worksheet.getCell(5, colIndex);
    cell.value = item.name;
    cell.style = {
      font: { bold: true },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F4EA' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: THIN_BORDER
    };

    if (colspan > 1) {
      for (let i = colIndex + 1; i <= endCol; i++) {
        const mergeCell = worksheet.getCell(5, i);
        mergeCell.style.border = THIN_BORDER;
      }
    }

    colIndex += colspan;
  });

  // 二级数据
  const dataToShow = secondData.length === 0
    ? [Array(totalCols).fill('暂无数据')]
    : secondData;

  dataToShow.forEach((row, rowIndex) => {
    const excelRow = rowIndex + 6;
    for (let i = 0; i < totalCols; i++) {
      const cell = worksheet.getCell(excelRow, i + 1);
      cell.value = row[i] !== undefined ? row[i] : '';
      cell.style = {
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: THIN_BORDER
      };
    }
  });

  // 设置列宽
  for (let i = 1; i <= totalCols; i++) {
    worksheet.getColumn(i).width = 15;
  }

  // 导出文件
  try {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (e) {
    console.error('导出失败：', e);
    alert('Excel导出失败，请检查数据！');
  }
}